import { Card, Typography } from '@growdash/design-system/atoms';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Loader2 } from 'lucide-react';
// @ts-ignore
import AishaCard from '@growdash/growintel/components/atoms/aisha-card';
import { Brand, UniqueMenuItem } from '@growdash/growintel/generated';
import { useGrowintel } from '@growdash/growintel/hooks/useGrowIntel';
import { executeFacadeRequest } from '@growdash/growintel/lib/aisha';
import ReactMarkDown from 'react-markdown';

type Props = {
  isEmptyState?: boolean;
  isLoadingBrands?: boolean;
  firstBrandData?:
    | {
        brand: Brand | undefined;
        items: UniqueMenuItem[] | undefined;
      }
    | undefined
    | null;
  secondBrandData?:
    | {
        brand: Brand | undefined;
        items: UniqueMenuItem[] | undefined;
      }
    | undefined
    | null;
};

export default function AishaComparisonView({
  isEmptyState,
  firstBrandData,
  secondBrandData,
  isLoadingBrands,
}: Props) {
  const { lib } = useGrowintel();
  const { aishaService } = lib;

  const enabled =
    !!firstBrandData?.brand?.id &&
    !!secondBrandData?.brand?.id &&
    !!firstBrandData?.items?.length &&
    !!secondBrandData?.items?.length;

  const {
    data: comparisonResult,
    isFetching,
    error,
  } = useQuery({
    queryKey: [
      'menu-comparison-ai',
      firstBrandData?.brand?.id,
      secondBrandData?.brand?.id,
    ],
    queryFn: async (): Promise<any | undefined> => {
      if (!firstBrandData?.brand || !firstBrandData?.brand || !aishaService)
        return null;
      try {
        const response: any = await executeFacadeRequest(
          'int-comp-antelope',
          aishaService,
          {
            brand_1: firstBrandData?.brand?.id,
            menu_1: JSON.stringify(firstBrandData.items),

            brand_2: secondBrandData?.brand?.id,
            menu_2: JSON.stringify(secondBrandData?.items),
          },
        );
        return response?.result;
      } catch (error) {
        console.error('error during menu review', error);
      }
    },
    enabled,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });
  if (isEmptyState) {
    return (
      <AishaCard>
        <Typography className="mt-4 font-semibold text-secondary">
          Pick a competitor to see where you stand
        </Typography>
        <Typography className="mt-2 text-tertiary text-sm">
          Select restaurants to start comparing menu items, price and get
          customized insights
        </Typography>
      </AishaCard>
    );
  }

  if (error) {
    return (
      <Card className="py-12 text-center flex items-center justify-center gap-2">
        <AlertCircle size={16} className="text-muted-foreground" />
        <p className="text-muted-foreground text-sm">
          Error occured during comparison analysis.
        </p>
      </Card>
    );
  }
  return (
    !error && (
      <>
        <AishaCard>
          {isFetching || isLoadingBrands ? (
            <div className="flex justify-center py-24 animate-pulse">
              <Loader2 className="animate-spin" />
            </div>
          ) : (
            <Typography className="animate-fade-in text-tertiary font-medium text-sm !leading-[1.25rem]">
              <ReactMarkDown>{comparisonResult}</ReactMarkDown>
            </Typography>
          )}
        </AishaCard>
      </>
    )
  );
}
